// React
import { useState, useRef } from 'react';

// Components
import { AddOutlined, CheckBoxOutlined, CloseOutlined, ExpandMoreOutlined, PersonAddOutlined, SearchOutlined } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Avatar, Box, Button, ClickAwayListener, Divider, Grow, IconButton, InputAdornment, MenuItem, MenuList, Paper, Popper, TextField, Tooltip, Typography } from '@mui/material';

// Style
import { blue, grey, red, orange } from '@mui/material/colors/index.js';
import { darkTheme } from '../../styles/colorSchemes/index.js';

// Hooks
import { useAppTheme } from '../../hooks/useAppTheme.js';

// Store
import useCoachStore from '../../store/coachStore.js';

// Utils
import { getConsistentAvatarColor } from '../../utils/index.js';

const CreateWeeklyScheduleAthletes = (props) => {
    const { athletes, checkedAthletes, styles, handleToggleAthlete } = props;
    const { setCheckedAthletes } = useCoachStore();
    const { isDarkMode } = useAppTheme();
    
    const primaryColor = isDarkMode ? orange[500] : blue[500];
    
    const [searchTerm, setSearchTerm] = useState('');
    const [openAddMenu, setOpenAddMenu] = useState(false);
    const [hoveredAthlete, setHoveredAthlete] = useState(null);
    const anchorRef = useRef(null);
    
    const convertBufferToBase64String = (athlete) => {
        return btoa(String.fromCharCode(...new Uint8Array(athlete.profile.picture.data)));
    }
    
    const handleToggleAddMenu = () => {
        setOpenAddMenu((prevOpen) => !prevOpen);
    };
    
    const handleCloseAddMenu = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpenAddMenu(false);
        setSearchTerm('');
    };
    
    const handleAddAthlete = (athlete) => {
        handleToggleAthlete(athlete);
        setOpenAddMenu(false);
        setSearchTerm('');
    };
    
    const handleSelectAll = () => {
        // Add all available athletes at once by merging with existing checked athletes
        const newCheckedAthletes = [...checkedAthletes, ...availableAthletes];
        setCheckedAthletes(newCheckedAthletes);
        setOpenAddMenu(false);
        setSearchTerm('');
    };
    
    // Filter athletes for dropdown: exclude already selected ones
    const availableAthletes = athletes
        .filter(athlete => 
            athlete.profile.lastName !== null && 
            !checkedAthletes.some(checked => checked.profile.email === athlete.profile.email) &&
            (
                athlete.profile.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                athlete.profile.firstName?.toLowerCase().includes(searchTerm.toLowerCase())
            )
        )
        .sort((a, b) => String(a.profile.lastName).localeCompare(String(b.profile.lastName)));

    return (
        <Accordion sx={{ background: isDarkMode ? darkTheme.accordionBg : grey[100], '&.MuiAccordion-root.Mui-expanded': { mb: '2px', mt: '2px' } }} defaultExpanded={ true }>
            
            <AccordionSummary expandIcon={ <ExpandMoreOutlined sx={{ color: isDarkMode ? darkTheme.textSecondary : undefined }} /> }>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Typography sx={{ color: isDarkMode ? darkTheme.textPrimary : undefined }}>Athleten</Typography>
                    <Typography sx={{ color: isDarkMode ? darkTheme.textSecondary : 'rgba(0, 0, 0, 1)', fontSize: '0.93rem' }}>
                        ({checkedAthletes.length})
                    </Typography>
                </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ bgcolor: isDarkMode ? darkTheme.headerBg : undefined }}>
                <Box sx={{ position: 'relative', pb: 1 }}>
                    <Tooltip title="Athlet hinzufügen">
                        <IconButton 
                            ref={anchorRef}
                            size="small"
                            onClick={handleToggleAddMenu}
                            sx={{ 
                                position: 'absolute',
                                top: -8,
                                right: 0,
                                zIndex: 10,
                                bgcolor: primaryColor,
                                color: 'white',
                                width: 32,
                                height: 32,
                                '&:hover': {
                                    bgcolor: isDarkMode ? orange[600] : blue[700],
                                }
                            }}
                        >
                            <AddOutlined sx={{ fontSize: 22 }} />
                        </IconButton>
                    </Tooltip>
                    
                    <Popper
                        open={openAddMenu}
                        anchorEl={anchorRef.current}
                        role={undefined}
                        placement="bottom-end"
                        transition
                        disablePortal
                        sx={{ zIndex: 1300 }}
                    >
                            {({ TransitionProps }) => (
                                <Grow
                                    {...TransitionProps}
                                    style={{
                                        transformOrigin: 'right top',
                                    }}
                                >
                                    <Paper
                                        elevation={8}
                                        sx={{
                                            mt: 1,
                                            minWidth: 300,
                                            maxWidth: 400,
                                            borderRadius: 2,
                                            overflow: 'hidden',
                                            boxShadow: isDarkMode ? '0 8px 32px rgba(0, 0, 0, 0.4)' : '0 8px 32px rgba(0, 0, 0, 0.15)',
                                            bgcolor: isDarkMode ? darkTheme.cardBg : undefined,
                                            border: isDarkMode ? `1px solid ${darkTheme.cardBorder}` : undefined,
                                        }}
                                    >
                                        <ClickAwayListener onClickAway={handleCloseAddMenu}>
                                            <Box>
                                                <Box sx={{ p: 2, bgcolor: isDarkMode ? darkTheme.headerBg : 'rgba(0, 0, 0, 0.02)' }}>
                                                    <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            placeholder="Athlet suchen..."
                                                            value={searchTerm}
                                                            onChange={(e) => setSearchTerm(e.target.value)}
                                                            autoFocus
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <SearchOutlined sx={{ fontSize: 20, color: isDarkMode ? darkTheme.textSecondary : 'action.active' }} />
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    borderRadius: 1.5,
                                                                    bgcolor: isDarkMode ? darkTheme.cardBg : 'white',
                                                                    '& fieldset': {
                                                                        borderColor: isDarkMode ? darkTheme.cardBorder : 'rgba(0, 0, 0, 0.23)',
                                                                    },
                                                                    '&:hover fieldset': {
                                                                        borderColor: primaryColor,
                                                                    },
                                                                    '&.Mui-focused fieldset': {
                                                                        borderColor: primaryColor,
                                                                    },
                                                                },
                                                                '& .MuiInputBase-input': {
                                                                    color: isDarkMode ? darkTheme.textPrimary : undefined,
                                                                },
                                                                '& .MuiInputBase-input::placeholder': {
                                                                    color: isDarkMode ? darkTheme.textSecondary : undefined,
                                                                    opacity: 0.7,
                                                                },
                                                            }}
                                                        />
                                                    </Box>
                                                    {availableAthletes.length > 0 && (
                                                        <Button
                                                            fullWidth
                                                            size="small"
                                                            variant="outlined"
                                                            startIcon={<CheckBoxOutlined />}
                                                            onClick={handleSelectAll}
                                                            sx={{
                                                                textTransform: 'none',
                                                                borderRadius: 1.5,
                                                                borderColor: isDarkMode ? darkTheme.cardBorder : 'rgba(0, 0, 0, 0.23)',
                                                                color: isDarkMode ? darkTheme.textPrimary : 'text.primary',
                                                                '&:hover': {
                                                                    borderColor: primaryColor,
                                                                    bgcolor: isDarkMode ? `${primaryColor}22` : blue[50],
                                                                }
                                                            }}
                                                        >
                                                            Alle auswählen ({availableAthletes.length})
                                                        </Button>
                                                    )}
                                                </Box>
                                                <Divider sx={{ borderColor: isDarkMode ? darkTheme.cardBorder : undefined }} />
                                                <MenuList
                                                    sx={{
                                                        maxHeight: 300,
                                                        overflow: 'auto',
                                                        p: 1,
                                                        bgcolor: isDarkMode ? darkTheme.cardBg : undefined,
                                                    }}
                                                >
                                                    {availableAthletes.length === 0 ? (
                                                        <Box sx={{ p: 2, textAlign: 'center' }}>
                                                            <Typography variant="body2" sx={{ color: isDarkMode ? darkTheme.textSecondary : 'text.secondary' }}>
                                                                {searchTerm ? 'Keine Athleten gefunden' : 'Alle Athleten ausgewählt'}
                                                            </Typography>
                                                        </Box>
                                                    ) : (
                                                        availableAthletes.map((athlete, index) => (
                                                            <MenuItem
                                                                key={athlete.profile.email}
                                                                onClick={() => handleAddAthlete(athlete)}
                                                                sx={{
                                                                    borderRadius: 1.5,
                                                                    mb: 0.5,
                                                                    '&:hover': {
                                                                        bgcolor: isDarkMode ? darkTheme.cardHover : blue[50],
                                                                    }
                                                                }}
                                                            >
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                                                                    <Avatar
                                                                        src={`data:image/png;base64,${convertBufferToBase64String(athlete)}`}
                                                                        sx={{
                                                                            width: 36,
                                                                            height: 36,
                                                                            bgcolor: getConsistentAvatarColor(athlete),
                                                                            fontSize: '0.875rem',
                                                                        }}
                                                                    />
                                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                        <Typography variant="body2" fontWeight={500} noWrap sx={{ color: isDarkMode ? darkTheme.textPrimary : undefined }}>
                                                                            {athlete.profile.lastName}, {athlete.profile.firstName}
                                                                        </Typography>
                                                                        <Typography variant="caption" noWrap sx={{ color: isDarkMode ? darkTheme.textSecondary : 'text.secondary' }}>
                                                                            {athlete.squad}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            </MenuItem>
                                                        ))
                                                    )}
                                                </MenuList>
                                            </Box>
                                        </ClickAwayListener>
                                    </Paper>
                                </Grow>
                            )}
                        </Popper>

                    <Box 
                        className={styles.weeklyathletes}
                        sx={{ 
                            minHeight: '15vh',
                            maxHeight: '30vh',
                            overflow: 'auto',
                            pr: 1
                        }}
                    >
                        { checkedAthletes.length <= 0 && (
                            <Box sx={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                height: '100%',
                                gap: 1
                            }}>
                                <PersonAddOutlined sx={{ fontSize: 48, color: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)' }} />
                                <Typography sx={{ color: isDarkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)' }} variant='body2' textAlign='center'>
                                    Keine Athleten ausgewählt
                                </Typography>
                            </Box>
                        )}

                        { checkedAthletes.length > 0 && (
                            <Box sx={{ 
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                                gap: 0,
                                px: 1,
                                pt: 1.5,
                                pb: 1
                            }}>
                                {checkedAthletes
                                    .filter(athlete => athlete.profile.lastName !== null)
                                    .sort((a, b) => String(a.profile.lastName).localeCompare(String(b.profile.lastName)))
                                    .map((athlete) => {
                                        const isHovered = hoveredAthlete === athlete.profile.email;
                                        
                                        return (
                                            <Box
                                                key={athlete.profile.email}
                                                onMouseEnter={() => setHoveredAthlete(athlete.profile.email)}
                                                onMouseLeave={() => setHoveredAthlete(null)}
                                                sx={{
                                                    position: 'relative',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    pt: 1,
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                <Box sx={{ position: 'relative' }}>
                                                    <Avatar
                                                        src={`data:image/png;base64,${convertBufferToBase64String(athlete)}`}
                                                        sx={{
                                                            width: 56,
                                                            height: 56,
                                                            bgcolor: getConsistentAvatarColor(athlete),
                                                            color: isDarkMode ? darkTheme.accordionBg : 'white',
                                                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                                            transition: 'all 0.2s ease',
                                                        }}
                                                    >
                                                        
                                                    </Avatar>
                                                    
                                                    {isHovered && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleToggleAthlete(athlete)}
                                                            sx={{
                                                                position: 'absolute',
                                                                top: -8,
                                                                right: -8,
                                                                bgcolor: red[500],
                                                                color: 'white',
                                                                width: 24,
                                                                height: 24,
                                                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                                                                '&:hover': {
                                                                    bgcolor: red[700],
                                                                }
                                                            }}
                                                        >
                                                            <CloseOutlined sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                                
                                                <Typography 
                                                    variant="caption" 
                                                    fontWeight={500}
                                                    sx={{ 
                                                        textAlign: 'center',
                                                        lineHeight: 1.2,
                                                        wordBreak: 'break-word',
                                                        fontSize: '0.75rem',
                                                        maxWidth: '100px',
                                                        color: isDarkMode ? darkTheme.textPrimary : undefined
                                                    }}
                                                >
                                                    {athlete.profile.lastName}, {athlete.profile.firstName}
                                                </Typography>
                                                <Typography 
                                                    variant="caption" 
                                                    sx={{ 
                                                        textAlign: 'center',
                                                        lineHeight: 0.6,
                                                        fontSize: '0.67rem',
                                                        maxWidth: '100px',
                                                        color: isDarkMode ? darkTheme.textSecondary : 'text.secondary'
                                                    }}
                                                >
                                                    {athlete.squad}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                            </Box>
                        )}
                    </Box>
                </Box>
            </AccordionDetails>
        </Accordion>
    )
}

export default CreateWeeklyScheduleAthletes;
